import { useCallback, useState } from 'react';
import { useQuery } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';

import { Select } from '@m/alchemy-ui/Select';
import { Button } from '@m/alchemy-ui/Button';
import { LoadingIndicator } from '@m/alchemy-ui/LoadingIndicator';
import { GET_USER_IDEAS_AND_COUNT } from '../../graphql/queries';
import { IdeaCard, type IdeaRow } from './IdeaCard';
import * as Styled from './MyIdeas.styled';

const IDEAS_PER_PAGE = 20;

type SortOption = 'newest' | 'mostPopular' | 'mostComments';

interface SortConfig {
  order: string[][];
  key: SortOption;
}

const SORT_OPTIONS: Record<SortOption, string[][]> = {
  newest: [['createdAt', 'DESC']],
  mostPopular: [['voteCount', 'DESC']],
  mostComments: [['commentCount', 'DESC']]
};

interface UserIdeasAndCountData {
  userIdeasAndCount: {
    count: number;
    rows: IdeaRow[];
  };
}

export interface MyIdeasProps {
  readonly userId: number;
  readonly isOwnProfile: boolean;
}

export const MyIdeas = ({ userId, isOwnProfile }: MyIdeasProps) => {
  const intl = useIntl();
  const [sort, setSort] = useState<SortConfig>({ order: SORT_OPTIONS.newest, key: 'newest' });

  const { data, loading, fetchMore } = useQuery<UserIdeasAndCountData>(GET_USER_IDEAS_AND_COUNT, {
    variables: {
      userId,
      limit: IDEAS_PER_PAGE,
      offset: 0,
      order: sort.order
    },
    fetchPolicy: 'cache-and-network'
  });

  const ideas = data?.userIdeasAndCount?.rows ?? [];
  const totalCount = data?.userIdeasAndCount?.count ?? 0;
  const hasMore = ideas.length < totalCount;

  const handleSortChange = useCallback((option: { value: SortOption }) => {
    setSort({ order: SORT_OPTIONS[option.value], key: option.value });
  }, []);

  const handleLoadMore = useCallback(() => {
    fetchMore({
      variables: { offset: ideas.length },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        const existingIds = new Set(prev.userIdeasAndCount.rows.map((r) => r.id));
        const newRows = fetchMoreResult.userIdeasAndCount.rows.filter((r) => !existingIds.has(r.id));
        return {
          userIdeasAndCount: {
            ...fetchMoreResult.userIdeasAndCount,
            rows: [...prev.userIdeasAndCount.rows, ...newRows]
          }
        };
      }
    });
  }, [fetchMore, ideas.length]);

  const sortOptions = [
    {
      label: intl.formatMessage({ id: 'newest', defaultMessage: 'Newest' }),
      value: 'newest' as SortOption
    },
    {
      label: intl.formatMessage({ id: 'topVoted', defaultMessage: 'Top voted' }),
      value: 'mostPopular' as SortOption
    },
    {
      label: intl.formatMessage({ id: 'mostDiscussed', defaultMessage: 'Most discussed' }),
      value: 'mostComments' as SortOption
    }
  ];

  if (loading && ideas.length === 0) {
    return (
      <Styled.LoadingContainer>
        <LoadingIndicator />
      </Styled.LoadingContainer>
    );
  }

  return (
    <div>
      <Styled.FilterBar>
        <span>
          <FormattedMessage id="showing" defaultMessage="Showing" />
          {': '}
          <FormattedMessage
            id="resultCount"
            defaultMessage="{count, plural, one {# Result} other {# Results}}"
            values={{ count: ideas.length }}
          />
        </span>

        <Styled.SortContainer>
          <Styled.SortLabel>
            <FormattedMessage id="sortBy" defaultMessage="Sort by" />
          </Styled.SortLabel>
          <Select defaultValue={sort.key} options={sortOptions} onChange={handleSortChange} />
        </Styled.SortContainer>
      </Styled.FilterBar>

      {ideas.length === 0 ? (
        <Styled.EmptyState>
          {isOwnProfile ? (
            <FormattedMessage id="noIdeasOwn" defaultMessage="You haven't posted any ideas yet." />
          ) : (
            <FormattedMessage id="noIdeas" defaultMessage="No posts found." />
          )}
        </Styled.EmptyState>
      ) : (
        <>
          <Styled.IdeaList>
            {ideas.map((idea) => (
              <IdeaCard key={idea.id} idea={idea} isOwnProfile={isOwnProfile} />
            ))}
          </Styled.IdeaList>

          {hasMore && (
            <Styled.LoadMoreContainer>
              <Button isLoading={loading} onClick={handleLoadMore}>
                <FormattedMessage id="loadMore" defaultMessage="Load more" />
              </Button>
            </Styled.LoadMoreContainer>
          )}
        </>
      )}
    </div>
  );
};
